import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, CalendarDays } from 'lucide-react';
import api from '../../services/api';
import { Campo, chiave, annunciaSchedaSalvata } from './CampiScheda';
import BottoneRefertoDaScheda from './BottoneRefertoDaScheda';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import '../../styles/SchedeProcedura.css';
import '../../styles/SchedeProfilo.css';

/**
 * Una scheda clinica compilata nel profilo del paziente.
 *
 * QUI LA SCHEDA SI RIPETE NEL TEMPO, e cambia tutto rispetto alla
 * procedura: l'analisi di marzo e quella di settembre sono due cose
 * diverse, la seconda non sovrascrive la prima, e si confrontano. Per
 * questo la prima cosa che si vede e' l'elenco delle date: aprire
 * direttamente l'ultima nasconderebbe che le altre esistono.
 *
 * Il disegno dei campi e' lo stesso della procedura - stesso file - cosi'
 * un campo si comporta allo stesso modo ovunque lo si incontri.
 */

const dataIta = (v) => {
    if (!v) return '';
    const d = new Date(String(v).slice(0, 10));
    return isNaN(d) ? '' : d.toLocaleDateString('it-IT');
};

const oggiIso = () => new Date().toISOString().slice(0, 10);

const SchedeProfilo = ({ pazienteId, schedaCodice }) => {
    const [scheda, setScheda] = useState(null);
    const [compilazioni, setCompilazioni] = useState([]);
    const [apertaId, setApertaId] = useState(null);
    const [aperta, setAperta] = useState(null);
    const [valori, setValori] = useState({});
    const [righe, setRighe] = useState({});
    /* «Modificato» = diverso dall'istantanea presa aprendo la compilazione
       e a ogni salvataggio riuscito (utils/uscitaProtetta.js). */
    const [versione, setVersione] = useState(0);
    const sporco = useSporco({ valori, righe }, versione);
    useUscitaProtetta(sporco);
    const [salvando, setSalvando] = useState(false);
    const [salvata, setSalvata] = useState(false);
    const [errore, setErrore] = useState(null);
    const [dataNuova, setDataNuova] = useState(oggiIso());

    const caricaElenco = useCallback(async () => {
        if (!pazienteId) return;
        try {
            const r = await api.get(`/schede/profilo/${pazienteId}`);
            const s = (r.data?.data?.schede || []).find((x) => x.codice === schedaCodice);
            setScheda(s || null);
            setCompilazioni(s?.compilazioni || []);
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere le compilazioni');
        }
    }, [pazienteId, schedaCodice]);

    useEffect(() => { caricaElenco(); }, [caricaElenco]);

    /* Cambiando scheda si chiude quella aperta: restare su una
       compilazione della scheda precedente sarebbe il modo piu' rapido di
       scrivere nel posto sbagliato. */
    useEffect(() => { setApertaId(null); setAperta(null); }, [schedaCodice]);

    const apri = useCallback(async (id) => {
        try {
            const r = await api.get(`/schede/profilo/compilazione/${id}`);
            const d = r.data?.data || {};
            setAperta(d.scheda || null);
            setValori(d.valori || {});
            setRighe(d.righe || {});
            setVersione((v) => v + 1);
            setApertaId(id);
            setSalvata(false);
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco ad aprire la compilazione');
        }
    }, []);

    const nuova = async () => {
        try {
            const r = await api.post(`/schede/profilo/${pazienteId}/${scheda.id}`, { data: dataNuova });
            await caricaElenco();
            await apri(r.data.data.compilazione.id);
        } catch (e) {
            setErrore(e?.message || 'Non riesco a creare la compilazione');
        }
    };

    const quanteRighe = (schedaId) => Math.max(1, righe[schedaId] || 1);

    const cambia = (campoId, riga, v) => {
        setValori((p) => ({ ...p, [chiave(campoId, riga)]: v }));
        setSalvata(false);
    };

    const salva = async () => {
        setSalvando(true);
        setErrore(null);
        try {
            const voci = [];
            for (const sez of aperta.sezioni) {
                const n = sez.ripetibile ? quanteRighe(aperta.id) : 1;
                for (let riga = 1; riga <= n; riga++) {
                    for (const c of sez.campi) {
                        const k = chiave(c.campo_id, riga);
                        if (!(k in valori)) continue;
                        voci.push({ campo_id: c.campo_id, riga, valore: valori[k] });
                    }
                }
            }
            if (!voci.length) { setSalvando(false); return; }
            await api.put(`/schede/profilo/compilazione/${apertaId}`, { valori: voci });
            annunciaSchedaSalvata();
            setSalvata(true);
            await apri(apertaId);
        } catch (e) {
            setErrore(e?.message || 'Salvataggio non riuscito');
        } finally {
            setSalvando(false);
        }
    };

    const togliRiga = async (riga) => {
        if (!window.confirm(`Togliere la ripetizione ${riga}?`)) return;
        try {
            await api.delete(`/schede/profilo/compilazione/${apertaId}/riga/${riga}`);
            await apri(apertaId);
            setRighe((p) => ({ ...p, [aperta.id]: Math.max(1, quanteRighe(aperta.id) - 1) }));
        } catch (e) {
            setErrore(e?.message || 'Non riesco a togliere la ripetizione');
        }
    };

    if (!scheda) {
        return <p className="spf-vuoto">{errore || 'Scheda non disponibile per questo paziente.'}</p>;
    }

    return (
        <div className="spf">
            <div className="spf-testa">
                <div>
                    <h3>{scheda.nome}</h3>
                    {scheda.area_nome && <span>{scheda.area_nome}</span>}
                </div>
                <div className="spf-nuova">
                    <label>
                        <CalendarDays size={14} aria-hidden="true" />
                        <input type="date" value={dataNuova}
                               onChange={(e) => setDataNuova(e.target.value)} />
                    </label>
                    <button type="button" onClick={nuova}>
                        <Plus size={14} aria-hidden="true" /> Nuova compilazione
                    </button>
                </div>
            </div>

            {errore && <p className="sp-errore">{errore}</p>}

            {compilazioni.length === 0 ? (
                <p className="spf-vuoto">
                    Questa scheda non è ancora stata compilata per questo paziente.
                    Scegli la data e premi <b>Nuova compilazione</b>.
                </p>
            ) : (
                <div className="spf-date">
                    {compilazioni.map((c) => (
                        <button type="button" key={c.id}
                                className={`spf-data ${apertaId === c.id ? 'attiva' : ''}`}
                                onClick={() => apri(c.id)}>
                            {dataIta(c.data)}
                        </button>
                    ))}
                </div>
            )}

            {aperta && (
                <div className="spf-scheda">
                    {aperta.sezioni.filter((z) => z.campi.length > 0).map((sez) => (
                        <div className="sp-sezione" key={sez.id}>
                            <div className="sp-sezione-titolo">{sez.titolo}</div>
                            {sez.ripetibile ? (
                                <>
                                    {Array.from({ length: quanteRighe(aperta.id) }, (_, i) => i + 1).map((riga) => (
                                        <div className="sp-ripetizione" key={riga}>
                                            <div className="sp-ripetizione-testa">
                                                <span>{sez.ripetibile_etichetta} {riga}</span>
                                                {quanteRighe(aperta.id) > 1 && (
                                                    <button type="button" onClick={() => togliRiga(riga)}
                                                            title="Togli questa ripetizione">
                                                        <Trash2 size={13} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="sp-campi">
                                                {sez.campi.map((c) => (
                                                    <Campo key={c.campo_id} campo={c} riga={riga}
                                                           valore={valori[chiave(c.campo_id, riga)]}
                                                           onChange={(v) => cambia(c.campo_id, riga, v)} />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" className="sp-aggiungi"
                                            onClick={() => setRighe((p) => ({ ...p, [aperta.id]: quanteRighe(aperta.id) + 1 }))}>
                                        <Plus size={13} aria-hidden="true" /> Aggiungi {sez.ripetibile_etichetta?.toLowerCase()}
                                    </button>
                                </>
                            ) : (
                                <div className="sp-campi">
                                    {sez.campi.map((c) => (
                                        <Campo key={c.campo_id} campo={c} riga={1}
                                               valore={valori[chiave(c.campo_id, 1)]}
                                               onChange={(v) => cambia(c.campo_id, 1, v)} />
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}

                    <div className="sp-azioni">
                        {salvata && <span className="sp-salvata">Salvata</span>}
                        <BottoneRefertoDaScheda compilazioneId={apertaId} disabilitato={salvando} />
                        <button type="button" onClick={salva} disabled={salvando}>
                            {salvando ? 'Salvo…' : 'Salva la scheda'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchedeProfilo;
